import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { ICreateBatchData } from 'src/app/models/create-batch-data';
import { IListContract } from 'src/app/models/verification';
import { DocumentValidator } from 'src/app/shared/validators/document.validator';
import { ApiContractService } from 'src/app/services/api/api-contract.service';
import { DataService } from 'src/app/services/data.service';
import { StatusBadgeTone } from 'src/app/shared/components/status-badge/status-badge.component';
import { contractStatusTone } from 'src/app/shared/utils/status-badge.util';

@Component({
  selector: 'app-esteira-contract-card',
  templateUrl: './esteira-contract-card.component.html',
  styleUrls: ['./esteira-contract-card.component.scss']
})
export class EsteiraContractCardComponent implements OnInit, OnChanges {
  private readonly productNamesById: Record<string, string> = {
    '00000000-0000-0000-0000-000000000001': 'Isolado',
    '00000000-0000-0000-0000-000000000002': 'Home Equity',
    '00000000-0000-0000-0000-000000000003': 'Portabilidade',
    '00000000-0000-0000-0000-000000000004': 'Consórcio',
    '00000000-0000-0000-0000-000000000005': 'Empréstimo Pessoal',
    '00000000-0000-0000-0000-000000000006': 'Repasse',
    '00000000-0000-0000-0000-000000000007': 'Construcasa',
    '00000000-0000-0000-0000-000000000008': 'Piloto'
  };

  @Input() contract!: IListContract;
  @Input() currentContext: 'invoice' | 'transfer' = 'invoice';
  @Input() linha!: number | undefined;
  @Input() checkedContracts: IListContract[] = [];

  contractForm!: FormGroup;
  modeUrl = 'visualizar';

  constructor(
    private _dataService: DataService,
    private _apiContractService: ApiContractService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    this.setContractCheckedSubscriber();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildForm();

    if (this.contractForm) {
      this.contractForm.patchValue(this.contract);
    }
  }

  buildForm(): void {
    this.contractForm = new FormGroup({
      Checked: new FormControl(false),
      Disabled: new FormControl(false),
      Chanel: new FormControl(null),
      CompanyId: new FormControl(null),
      ContractDate: new FormControl(null),
      RegistertDate: new FormControl(null),
      ContractId: new FormControl(null),
      ContractStatus: new FormControl(null),
      ContractType: new FormControl(null),
      Error: new FormControl(null),
      FinancialInstitution: new FormControl(null),
      Incomplete: new FormControl(null),
      Number: new FormControl(null),
      Status: new FormControl(null)
    });
  }

  get checkedControl(): boolean {
    return this.checkedContracts.some(
      (ctt) => ctt.ContractId === this.contract.ContractId
    );
  }

  checkUncheckContract(contract: IListContract) {
    this._apiContractService.setContractsArray(contract);
  }

  setContractCheckedSubscriber(): void {
    this._apiContractService.getContractsArray().subscribe({
      next: (contracts) => {

        let createBatch: ICreateBatchData = {
          showCreateBatchButton: contracts.length == 1
        };

        if (createBatch.showCreateBatchButton)
          createBatch.contractId = contracts[0].ContractId;

        this._dataService.setCreateBatchData(createBatch);
      }
    });

    this._dataService.clearContracts.subscribe({
      next: () => {
        this._apiContractService.clearContractsSelection();
      }
    });


  }

  formatStatusText(text: string): string {
    const status = (text || '').trim();
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'approval') return 'Aprovação';
    if (normalizedStatus === 'pre-faturado') return 'Pré-faturado';
    if (normalizedStatus === 'status - contrato elaborado') return 'Elaborado';
    if (normalizedStatus === 'status - contrato registrado') return 'Registrado';
    if (normalizedStatus === 'status - contrato cancelado') return 'Cancelado';

    return status;
  }

  getContractStatusTone(): StatusBadgeTone {
    return contractStatusTone(this.formatStatusText(this.contract?.StatusOverride), {
      toReverse: (this.contract as IListContract & { ToReverse?: boolean })?.ToReverse === true
    });
  }

  isWarningError(): boolean {
    return !!this.contract?.MsgError?.startsWith('WARNING');
  }

  getErrorTooltip(): string {
    const message = this.contract?.MsgError?.replace('WARNING:', '').trim();

    if (this.isWarningError()) {
      return message || 'Contrato com aviso. Verifique os detalhes antes de seguir.';
    }

    if (message) {
      return `Contrato com erro que exige correcao. ${message}`;
    }

    return 'Contrato com erro que exige correcao antes do processamento.';
  }

  getPartnerName(): string {
    return (this.contract?.Partner || this.contract?.BeneficiaryName || '').trim();
  }

  getPartnerDocument(): string {
    const contractData = this.contract as IListContract & {
      partnerDocument?: string;
      beneficiaryDocument?: string;
      Document?: string;
      document?: string;
    };

    const rawValue = contractData?.PartnerDocument
      ?? contractData?.partnerDocument
      ?? contractData?.BeneficiaryDocument
      ?? contractData?.beneficiaryDocument
      ?? contractData?.Document
      ?? contractData?.document
      ?? '';

    const raw = String(rawValue).trim();
    return raw ? DocumentValidator.formatCpfCnpj(raw) : '';
  }

  private getStatusClassByValue(status: string): string {
    const normalizedStatus = status.toLowerCase().trim();

    if (!normalizedStatus || normalizedStatus === '-' || normalizedStatus === 'sem lote') {
      return 'invoicing-pill-neutral';
    }

    if (['faturado', 'refaturado', 'pago', 'integrado', 'concluido', 'concluído', 'aprovado', 'liberado', 'liberado parcialmente'].includes(normalizedStatus)) {
      return 'invoicing-pill-success';
    }

    if (['revertido', 'reverso', 'reversed', 'estornado', 'cancelado', 'erro'].includes(normalizedStatus)) {
      return 'invoicing-pill-danger';
    }

    if (['em andamento', 'andamento', 'processando', 'aguardando', 'aguardando aprovacao', 'aguardando aprovação'].includes(normalizedStatus)) {
      return 'invoicing-pill-info';
    }

    return 'invoicing-pill-warn';
  }

  getContractProductLabel(): string {
    const contractData = this.contract as IListContract & {
      productName?: string;
      productId?: string;
      contractType?: string;
    };

    const productName = (contractData.ProductName || contractData.productName || '').trim();

    if (productName) {
      return productName;
    }

    const productId = (contractData.ProductId || contractData.productId || '').toLowerCase().trim();
    const productNameById = this.productNamesById[productId];

    if (productNameById) {
      return productNameById;
    }

    const contractType = (contractData.ContractType || contractData.contractType || '').trim();

    return contractType || '-';
  }

  getInvoicingStatusLabel(): string {
    if (!this.getInvoiceBatchNumber()) {
      return 'Sem lote';
    }

    const rawStatus = this.currentContext === 'invoice'
      ? this.toText(this.getRawInvoicingStatus())
      : this.toText(this.contract?.InvoiceBatchStatus);

    return this.formatStatusText(rawStatus) || '-';
  }

  getInvoicingStatusClass(): string {
    return this.getStatusClassByValue(this.getInvoicingStatusLabel());
  }

  getTransferStatusLabel(): string {
    if (!this.getTransferBatchDisplayNumber()) {
      return 'Sem lote';
    }

    const rawStatus = this.currentContext === 'transfer'
      ? this.toText(this.getRawInvoicingStatus() || this.contract?.TransferBatchStatus)
      : this.toText(this.contract?.TransferBatchStatus);

    return this.formatStatusText(rawStatus) || '-';
  }

  getTransferStatusClass(): string {
    return this.getStatusClassByValue(this.getTransferStatusLabel());
  }

  isContractInactive(): boolean {
    return this.contract?.IsActive === false || this.contract?.Active === false;
  }

  getContractReverseBadgeLabel(): string | null {
    if (this.isInvoicingStatusReversedCode()) {
      return 'Estornado';
    }

    if (this.hasExplicitToReverseFlag()) {
      return 'Estornar';
    }

    return null;
  }

  private isInvoicingStatusReversedCode(): boolean {
    const statusCode = this.getRawInvoicingStatusCode();

    if (statusCode === null) {
      return false;
    }

    return statusCode === 3;
  }

  private hasExplicitToReverseFlag(): boolean {
    const contractData = this.contract as IListContract & {
      toReverse?: boolean;
      InvoiceToReverse?: boolean;
      invoiceToReverse?: boolean;
    };

    return contractData.ToReverse === true
      || contractData.toReverse === true
      || contractData.InvoiceToReverse === true
      || contractData.invoiceToReverse === true;
  }

  private getRawInvoicingStatus(): string | number | null | undefined {
    const contractData = this.contract as IListContract & {
      invoicingStatus?: string | number;
    };

    return contractData.InvoicingStatus
      ?? contractData.invoicingStatus;
  }

  private getRawInvoicingStatusCode(): number | null {
    const contractData = this.contract as IListContract & {
      InvoicingStatusCode?: number;
      invoicingStatusCode?: number;
      invoicingStatus?: string | number;
    };

    const rawCandidates: Array<string | number | null | undefined> = [
      contractData.InvoicingStatusCode,
      contractData.invoicingStatusCode,
      contractData.InvoicingStatus,
      contractData.invoicingStatus
    ];

    for (const rawValue of rawCandidates) {
      if (rawValue === null || rawValue === undefined) {
        continue;
      }

      const normalizedValue = String(rawValue).trim();

      if (!normalizedValue) {
        continue;
      }

      const parsed = Number(normalizedValue);

      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  private toText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  getTransferBatchDisplayNumber(): string {
    const candidateParentBatchNumbers = [
      this.contract?.TransferParentBatchNumber,
      this.contract?.ParentTransferBatchNumber,
      this.contract?.ParentBatchNumber
    ];

    const parentBatchNumber = candidateParentBatchNumbers
      .map((value) => (value || '').trim())
      .find((value) => !!value);

    if (parentBatchNumber) {
      return parentBatchNumber;
    }

    return (this.contract?.TransferBatchNumber || '').trim();
  }

  getInvoiceBatchNumber(): string {
    return (this.contract?.InvoiceBatchNumber || '').trim();
  }

  getTransferAmount(): number {
    return typeof this.contract?.TransferAmount === 'number'
      ? this.contract.TransferAmount
      : 0;
  }

  hasTransferAmount(): boolean {
    return this.getTransferAmount() > 0;
  }

  navigateToContractEdit(contract: IListContract): void {
    this._router.navigate([
      '/contratos',
      contract.Number,
      contract.QueryNumber,
      contract.FinancialInstitution,
      'detalhes',
      this.modeUrl
    ]);
  }
}
